import { useEffect, useMemo, useState } from "react";
import { Button, Card, CircularProgress } from "@firecms/ui";
import { httpsCallable } from "firebase/functions";

import { AdminModal } from "../custom/AdminModal";
import {
  LEARNING_STAT_DIFFICULTIES,
  LEARNING_STATS_MIN_SAMPLE,
  type LearningCell,
  type LearningCellTone,
  type LearningQuestionRow,
  type LearningStatDifficulty,
  type LearningUnitRow,
  type LearningVerdict,
} from "../eventContent/aggregateLearningOverview";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import { useLearningStatsOverview } from "../hooks/useLearningStatsOverview";
import type { AdminTranslateFn } from "../i18n/getAdminT";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { EventCollectionId } from "../workspace/collectionIds";
import {
  statsMuted,
  statsPageTitle,
  statsSectionTitle,
} from "./statsViewTheme";

type LearningStatsViewProps = {
  eventId: EventCollectionId;
};

type SelectedCell = {
  unit: LearningUnitRow;
  difficulty: LearningStatDifficulty;
  cell: LearningCell;
};

function cooldownRemainingMs(
  compute: { status?: string; cooldownUntil?: Date } | null,
  nowMs: number
): number {
  if (!compute?.cooldownUntil || compute.status === "running") {
    return 0;
  }
  const remaining = compute.cooldownUntil.getTime() - nowMs;
  return remaining > 0 ? remaining : 0;
}

function formatPercent(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return `${value}%`;
}

function cellToneClass(tone: LearningCellTone): string {
  switch (tone) {
    case "good":
      return "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40";
    case "mixed":
      return "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40";
    case "poor":
      return "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40";
    case "insufficient":
      return "border-surface-200 bg-surface-100 dark:border-surface-600 dark:bg-surface-800";
    case "empty":
      return "border-dashed border-surface-200 bg-transparent dark:border-surface-700";
  }
}

function difficultyLabel(
  difficulty: LearningStatDifficulty,
  t: AdminTranslateFn
): string {
  return t(`learningStats.${difficulty}`);
}

function verdictText(verdict: LearningVerdict, t: AdminTranslateFn): string {
  switch (verdict.kind) {
    case "never":
      return t("learningStats.verdictNever");
    case "noAnswers":
      return t("learningStats.verdictNoAnswers");
    case "tooLittle":
      return t("learningStats.verdictTooLittle");
    case "goingWell":
      return t("learningStats.verdictGoingWell");
    case "weakest":
      return t("learningStats.verdictWeakest", {
        unit: verdict.cell.unitName,
        difficulty: difficultyLabel(verdict.cell.difficulty, t),
        percent: verdict.cell.accuracyPercent,
      });
  }
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 dark:border-surface-600 dark:bg-surface-800">
      <div className={`text-xs font-medium uppercase tracking-wide ${statsMuted}`}>
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-surface-900 dark:text-surface-100">
        {value}
      </div>
      {hint ? <div className={`mt-1 text-xs ${statsMuted}`}>{hint}</div> : null}
    </div>
  );
}

function QuestionsDialog({
  selected,
  onClose,
  t,
}: {
  selected: SelectedCell;
  onClose: () => void;
  t: AdminTranslateFn;
}) {
  const questions: LearningQuestionRow[] = selected.cell.questions;
  return (
    <AdminModal
      open
      onClose={onClose}
      ariaLabel={t("learningStats.questionsTitle")}
      title={t("learningStats.questionsTitle")}
      description={`${selected.unit.unitName} · ${difficultyLabel(selected.difficulty, t)}`}
      maxWidth="2xl"
      footer={
        <Button onClick={onClose} color="primary">
          {t("learningStats.close")}
        </Button>
      }
    >
      {questions.length === 0 ? (
        <p className={`text-sm ${statsMuted}`}>
          {selected.cell.tone === "empty"
            ? t("learningStats.noQuestions")
            : t("learningStats.noAnswersInCell")}
        </p>
      ) : (
        <ul className="space-y-3">
          {questions.map((question) => (
            <li
              key={question.questionId}
              className="rounded-lg border border-surface-200 px-3 py-3 dark:border-surface-600"
            >
              <div className="text-sm font-medium text-surface-900 dark:text-surface-100">
                {question.prompt}
              </div>
              <div className={`mt-1 font-mono text-xs ${statsMuted}`}>
                {question.questionCode}
              </div>
              <div className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs ${statsMuted}`}>
                <span>
                  {t("learningStats.accuracy")}:{" "}
                  {formatPercent(question.accuracyPercent)}
                </span>
                <span>
                  {t("learningStats.answersCount", {
                    count: question.answerCount,
                  })}
                </span>
                {question.topWrongLabel ? (
                  <span>
                    {t("learningStats.topWrong", {
                      option: question.topWrongLabel,
                      count: question.topWrongOptionCount ?? 0,
                    })}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </AdminModal>
  );
}

export function LearningStatsView({ eventId }: LearningStatsViewProps) {
  const { t, locale } = useAdminTranslation();
  const functions = useFirebaseFunctions();
  const { overview, loading, catalogLoaded, unpublished, compute } =
    useLearningStatsOverview(eventId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [selected, setSelected] = useState<SelectedCell | null>(null);

  const empty = catalogLoaded && overview.units.length === 0;
  const running = compute?.status === "running";
  const remainingCooldownMs = cooldownRemainingMs(compute, nowMs);
  const computeDisabled =
    queueing || running || remainingCooldownMs > 0 || unpublished || empty;

  useEffect(() => {
    if (!running && remainingCooldownMs <= 0) {
      return;
    }
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [running, remainingCooldownMs]);

  useEffect(() => {
    setConfirmOpen(false);
    setQueueError(null);
    setSelected(null);
  }, [eventId]);

  const statusMessage = useMemo(() => {
    if (running) {
      return `${t("learningStats.computeStatsRunning")} · ${compute?.progress ?? 0}%`;
    }
    if (compute?.status === "failed" && compute.error) {
      return t("learningStats.computeStatsFailed", { error: compute.error });
    }
    if (remainingCooldownMs > 0) {
      return t("learningStats.computeStatsCooldown", {
        seconds: Math.max(1, Math.ceil(remainingCooldownMs / 1000)),
      });
    }
    if (compute?.completedAt) {
      return t("learningStats.computeStatsLastRun", {
        when: compute.completedAt.toLocaleString(locale),
      });
    }
    return t("learningStats.computeStatsNever");
  }, [compute, locale, remainingCooldownMs, running, t]);

  const queueCompute = async () => {
    if (!functions) {
      setQueueError("Internal error");
      return;
    }
    setQueueing(true);
    setQueueError(null);
    try {
      const callable = httpsCallable(functions, "computeLearningStats");
      await callable({
        accountId: eventId.accountId,
        eventId: eventId.eventId,
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t("eventContent.unlockFailed");
      setQueueError(message);
    } finally {
      setQueueing(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className={statsPageTitle}>{t("learningStats.viewTitle")}</h1>
            <p className={`mt-2 text-sm ${statsMuted}`}>
              {t("learningStats.viewDescription")}
            </p>
          </div>
          {!unpublished && !empty ? (
            <Button
              variant="outlined"
              disabled={computeDisabled}
              onClick={() => setConfirmOpen(true)}
            >
              {queueing || running
                ? `${t("learningStats.computeStatsRunning")} ${compute?.progress ?? 0}%`
                : t("learningStats.computeStats")}
            </Button>
          ) : null}
        </div>

        {!unpublished && !empty ? (
          <p className={`mt-3 text-sm ${statsMuted}`}>
            {statusMessage}
            {queueError ? ` · ${queueError}` : ""}
          </p>
        ) : null}

        {loading ? (
          <div className="mt-6 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        ) : null}

        {!loading && empty ? (
          <p className={`mt-8 text-sm ${statsMuted}`}>
            {t(
              unpublished
                ? "learningStats.emptyUnpublished"
                : "learningStats.empty"
            )}
          </p>
        ) : null}

        {!loading && !empty ? (
          <>
            <p className="mt-6 text-base text-surface-800 dark:text-surface-200">
              {verdictText(overview.verdict, t)}
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatTile
                label={t("learningStats.accuracy")}
                value={formatPercent(overview.accuracyPercent)}
              />
              <StatTile
                label={t("learningStats.answered")}
                value={`${overview.answeredParticipantCount} / ${overview.participantCount}`}
                hint={t("learningStats.ofParticipants")}
              />
              <StatTile
                label={t("learningStats.weakest")}
                value={
                  overview.weakest
                    ? `${overview.weakest.unitCode} · ${difficultyLabel(overview.weakest.difficulty, t)} · ${overview.weakest.accuracyPercent}%`
                    : "—"
                }
              />
            </div>

            <Card className="mt-6 overflow-hidden border-surface-200 dark:border-surface-700 dark:bg-surface-800">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-surface-100 dark:bg-surface-900">
                    <tr>
                      <th className="px-4 py-3 font-medium">
                        {t("learningStats.unitColumn")}
                      </th>
                      {LEARNING_STAT_DIFFICULTIES.map((difficulty) => (
                        <th key={difficulty} className="px-4 py-3 font-medium">
                          {difficultyLabel(difficulty, t)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overview.units.map((unit) => (
                      <tr
                        key={unit.unitId}
                        className="border-t border-surface-200 dark:border-surface-700"
                      >
                        <td className="px-4 py-3 align-top">
                          <div className={statsSectionTitle}>{unit.unitName}</div>
                          <div className={`font-mono text-xs ${statsMuted}`}>
                            {unit.unitCode}
                          </div>
                        </td>
                        {LEARNING_STAT_DIFFICULTIES.map((difficulty) => {
                          const cell = unit.cells[difficulty];
                          const canOpen = cell.tone !== "empty";
                          return (
                            <td key={difficulty} className="px-3 py-3 align-top">
                              <button
                                type="button"
                                disabled={!canOpen}
                                onClick={() =>
                                  setSelected({ unit, difficulty, cell })
                                }
                                className={`w-full rounded-lg border px-3 py-3 text-left transition-colors ${cellToneClass(cell.tone)} ${
                                  canOpen
                                    ? "hover:brightness-95 disabled:cursor-default"
                                    : "cursor-default"
                                }`}
                              >
                                {cell.tone === "empty" ? (
                                  <div className={`text-xs ${statsMuted}`}>
                                    {t("learningStats.noQuestions")}
                                  </div>
                                ) : (
                                  <>
                                    <div className="text-xl font-semibold tabular-nums">
                                      {formatPercent(cell.accuracyPercent)}
                                    </div>
                                    <div className={`mt-1 text-xs ${statsMuted}`}>
                                      {t("learningStats.answersCount", {
                                        count: cell.answerCount,
                                      })}
                                      {" · "}
                                      {t("learningStats.peopleCount", {
                                        count: cell.uniqueParticipantCount,
                                      })}
                                    </div>
                                    {cell.tone === "insufficient" ? (
                                      <div className={`mt-1 text-xs ${statsMuted}`}>
                                        {t("learningStats.sampleTooSmall", {
                                          min: LEARNING_STATS_MIN_SAMPLE,
                                        })}
                                      </div>
                                    ) : null}
                                  </>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : null}
      </div>

      {selected ? (
        <QuestionsDialog
          selected={selected}
          onClose={() => setSelected(null)}
          t={t}
        />
      ) : null}

      {confirmOpen ? (
        <AdminModal
          open
          onClose={() => setConfirmOpen(false)}
          ariaLabel={t("learningStats.computeStatsConfirmTitle")}
          title={t("learningStats.computeStatsConfirmTitle")}
          description={t("learningStats.computeStatsConfirmBody")}
          maxWidth="lg"
          footer={
            <>
              <Button variant="text" onClick={() => setConfirmOpen(false)}>
                {t("learningStats.close")}
              </Button>
              <Button
                color="primary"
                disabled={queueing}
                onClick={() => void queueCompute()}
              >
                {t("learningStats.computeStats")}
              </Button>
            </>
          }
        />
      ) : null}
    </div>
  );
}
